import { Button } from "@/shared/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import type { Table } from "@tanstack/react-table";

export interface RemoteTablePaginationProps<TData> {
  table: Table<TData>;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalCount: number;
  allowedPageSizes: readonly number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  itemLabel?: string;
}

export function RemoteTablePagination<TData>({
  table,
  currentPage,
  totalPages,
  pageSize,
  totalCount,
  allowedPageSizes,
  onPageChange,
  onPageSizeChange,
  itemLabel = "Item(s)",
}: RemoteTablePaginationProps<TData>) {
  const canPreviousPage = table.getCanPreviousPage();
  const canNextPage = table.getCanNextPage();

  return (
    <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between border-t border-grid-200 bg-white px-4 py-2 z-10">
      <div className="text-sm text-ink-600">
        {totalCount.toLocaleString()} {itemLabel} found
      </div>
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2 text-sm text-ink-600">
          <span>Items per page</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-8 min-w-[80px] justify-between"
              >
                {pageSize}
                <ChevronDown className="h-4 w-4 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {allowedPageSizes.map((option) => (
                <DropdownMenuItem
                  key={option}
                  onClick={() => {
                    onPageSizeChange(option);
                    table.setPageSize(option);
                  }}
                  className={pageSize === option ? "bg-imperial-50" : ""}
                >
                  {option}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex items-center gap-2 text-sm text-ink-600">
          <span>
            Page {currentPage} of {totalPages || 1}
          </span>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => {
                table.setPageIndex(0);
                onPageChange(1);
              }}
              disabled={!canPreviousPage}
              aria-label="First page"
            >
              &lt;&lt;
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => {
                table.previousPage();
                onPageChange(currentPage - 1);
              }}
              disabled={!canPreviousPage}
              aria-label="Previous page"
            >
              &lt;
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => {
                table.nextPage();
                onPageChange(currentPage + 1);
              }}
              disabled={!canNextPage}
              aria-label="Next page"
            >
              &gt;
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => {
                table.setPageIndex(totalPages - 1);
                onPageChange(totalPages);
              }}
              disabled={!canNextPage}
              aria-label="Last page"
            >
              &gt;&gt;
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
